import { create } from "zustand"
import { buildFusionDraft } from "~/common/video/scriptFusion"
import { message } from "antd"
import { pollProductImageAnalysis } from "~/common/tasks/productImageAnalysis"
import { getRecreateProductInfo } from "~/common/video/recreateProductInfo"
import {
  DEFAULT_VIDEO_OPTIONS,
  getSafeVideoOptions,
  VIDEO_TOOL_LABELS,
} from "~/common/constants"
import { getHotVideoTaskId } from "~/common/tasks/hotVideoTask"
import {
  DEFAULT_BACKGROUND_MUSIC_MODE,
  getRecreateImageUrls,
  getRecreateReferenceSettings,
  RECREATE_IMAGE_LIMIT,
  type RecreateModel,
} from "~/common/video/recreate"
import { analyzeProductImage } from "~/api/material"
import { createHotVideoTask } from "~/api/task"

import type { HotVideoState } from "./types"
import {
  saveHotVideoChannel,
  getReferenceDefaults,
  getCustomModel,
  requestGuard,
  artlineRequestGuard,
  productRequestGuard,
  promptSaver,
  getPromptState,
  getInputFingerprint,
  bindScriptSaveState,
} from "./shared"
import { createTaskDraftActions } from "./taskDraft"
import { createModelArtlineActions } from "./modelArtline"
import { createScriptActions } from "./script"
import { createVideoActions } from "./video"

export type { BackgroundMusicMode } from "./types"
export {
  isHotVideoPromptStale,
  getHotVideoGenerationOptions,
  canUseHotVideoOriginalAudio,
} from "./shared"

export const useHotVideoStore = create<HotVideoState>((set, get) => ({
  staffPreview: null,
  importDraft: (task) => {
    const draft = buildFusionDraft(task)
    const { videoOptions } = get()
    get().clearTaskId()
    get().resetDraftFields()
    const fields = {
      videoUrl: draft.videoUrl,
      videoImageUrls: draft.videoImageUrls,
      productInfo: draft.productInfo,
      sourcePrompt: draft.sourcePrompt,
    }
    set({
      ...fields,
      ...getPromptState(draft.replicaPrompt),
      staffPreview: draft.preview,
      // 导入脚本随 create-video 提交，不伪造后端已保存的 optimize_prompt。
      savedReferenceSettings: getRecreateReferenceSettings({
        ...get(),
        ...fields,
      }),
      savedReferenceModel: { mode: "none" },
      replicaPromptInputFingerprint: getInputFingerprint(fields),
      videoOptions: { ...videoOptions, duration: draft.duration },
      status: "融合脚本已导入",
    })
  },

  productInfo: getRecreateProductInfo(),
  isAnalyzingProduct: false,
  productInfoError: "",
  setProductInfo: (value) => {
    productRequestGuard.invalidate()
    set((state) => ({
      productInfo: { ...state.productInfo, ...value },
      isAnalyzingProduct: false,
      productInfoError: "",
    }))
  },
  analyzeProductImages: async () => {
    const state = get()
    if (
      state.isAnalyzingProduct ||
      state.isRequesting ||
      state.isRestoringTaskDraft ||
      state.resourcesPending
    )
      return false
    const image_urls = getRecreateImageUrls(state.videoImageUrls)
    if (!image_urls.length) {
      void message.warning("请先上传商品素材")
      return false
    }
    if (image_urls.length > RECREATE_IMAGE_LIMIT) {
      void message.warning(`商品素材最多支持 ${RECREATE_IMAGE_LIMIT} 张`)
      return false
    }
    const isStale = productRequestGuard.begin(
      () =>
        get().draftRevision !== state.draftRevision ||
        get().videoImageUrls !== state.videoImageUrls,
    )
    set({ isAnalyzingProduct: true, productInfoError: "" })
    try {
      const response = await analyzeProductImage(
        { image_urls },
        { notify: false },
      )
      if (isStale()) return false
      if (!response.data) throw new Error("创建商品图分析任务失败")
      const result = await pollProductImageAnalysis(response.data, isStale)
      if (isStale() || !result) return false
      const current = get().productInfo
      set({
        productInfo: {
          name: result.name?.trim() || current.name,
          category: result.category?.trim() || current.category,
          targetUsers:
            result.target_users?.filter((value) => value.trim()).join("、") ||
            current.targetUsers,
          sellingPoints:
            result.selling_points?.filter((value) => value.trim()).join("\n") ||
            current.sellingPoints,
        },
      })
      void message.success("已根据商品图生成商品信息")
      return true
    } catch (error) {
      if (!isStale())
        set({
          productInfoError:
            error instanceof Error
              ? error.message
              : "商品图分析失败，请稍后重试",
        })
      return false
    } finally {
      if (!isStale()) set({ isAnalyzingProduct: false })
    }
  },
  ...getReferenceDefaults(),
  ...getPromptState(),
  status: "",
  taskErrorMessage: "",
  isRequesting: false,
  isRestoringTaskDraft: false,
  isSubmittingVideoTask: false,
  sourcePrompt: "",
  replicaPromptInputFingerprint: "",
  savedReferenceSettings: null,
  savedReferenceModel: null,
  isGeneratingModelArtline: false,
  modelArtlineError: "",
  hasEditedScript: false,
  canSaveScript: false,
  scriptSaveStatus: "idle",
  scriptSaveError: "",
  saveReplicaPrompt: () => promptSaver.flush(),
  scriptRevision: 0,
  showStoryboardValidation: false,
  draftRevision: 0,
  setClipRange: (start_time, end_time) => set({ start_time, end_time }),
  setReferenceModel: (referenceModel) => {
    const state = get()
    const imageChanged =
      referenceModel.mode === "custom" &&
      referenceModel.url !== getCustomModel(state)?.url
    if (imageChanged) artlineRequestGuard.invalidate()
    set({
      referenceModel,
      modelDrafts:
        state.referenceModel.mode === "none"
          ? state.modelDrafts
          : {
              ...state.modelDrafts,
              [state.referenceModel.mode]: state.referenceModel,
            },
      ...(imageChanged
        ? { isGeneratingModelArtline: false, modelArtlineError: "" }
        : {}),
    })
  },
  setReferenceModelMode: (mode) => {
    const state = get()
    if (mode === state.referenceModel.mode) return
    const model: RecreateModel =
      mode === "none"
        ? { mode }
        : (state.modelDrafts[mode] ??
          (mode === "system" ? { mode, model: null } : { mode, url: "" }))
    state.setReferenceModel(model)
  },
  setReferenceAudio: (referenceAudio) => set({ referenceAudio }),
  setResourcesPending: (resourcesPending) => set({ resourcesPending }),
  resetReferenceSettings: () =>
    set((state) => ({
      ...getReferenceDefaults(),
      isGeneratingModelArtline: false,
      modelArtlineError: "",
      draftRevision: state.draftRevision + 1,
    })),
  backgroundMusicMode: DEFAULT_BACKGROUND_MUSIC_MODE,
  creatingPromise: null,
  createdTaskId: "",
  taskId: "",
  videoDuration: undefined,
  videoImageUrls: "",
  videoOptions: DEFAULT_VIDEO_OPTIONS,
  videoUrl: "",

  clearTaskId: () => {
    promptSaver.reset()
    set({
      canSaveScript: false,
      createdTaskId: "",
      creatingPromise: null,
      taskId: "",
    })
  },

  ensureTaskId: async () => {
    const currentTaskId = get().taskId

    if (currentTaskId) return currentTaskId

    const currentPromise = get().creatingPromise

    if (currentPromise) return currentPromise

    const draftRevision = get().draftRevision

    const creatingPromise = createHotVideoTask()
      .then((response) => {
        if (
          get().creatingPromise !== creatingPromise ||
          get().draftRevision !== draftRevision
        )
          return null
        const nextTaskId = getHotVideoTaskId(response.data)

        if (!nextTaskId) {
          throw new Error(`创建${VIDEO_TOOL_LABELS.recreate}任务失败`)
        }

        set({ createdTaskId: nextTaskId, taskId: nextTaskId })
        void saveHotVideoChannel(nextTaskId, get().videoOptions.model)
        return nextTaskId
      })
      .catch((error) => {
        if (
          get().creatingPromise !== creatingPromise ||
          get().draftRevision !== draftRevision
        )
          return null
        void message.error(
          error instanceof Error
            ? error.message
            : `创建${VIDEO_TOOL_LABELS.recreate}任务失败`,
        )
        return null
      })
      .finally(() => {
        if (get().creatingPromise === creatingPromise)
          set({ creatingPromise: null })
      })

    set({ creatingPromise })

    return creatingPromise
  },

  resetDraftFields: () => {
    get().cancelRequests()
    get().resetScript()
    get().resetReferenceSettings()
    set({
      staffPreview: null,
      backgroundMusicMode: DEFAULT_BACKGROUND_MUSIC_MODE,
      productInfo: getRecreateProductInfo(),
      videoDuration: undefined,
      videoImageUrls: "",
      videoOptions: DEFAULT_VIDEO_OPTIONS,
      videoUrl: "",
    })
  },

  setBackgroundMusicMode: (backgroundMusicMode) => {
    set({ backgroundMusicMode })
  },

  setTaskId: (taskId) => {
    if (taskId !== get().taskId) {
      promptSaver.reset()
      set({ canSaveScript: false })
    }
    set((state) => ({
      taskId,
      ...(taskId !== state.taskId ? { creatingPromise: null } : {}),
    }))
  },

  setVideoDuration: (videoDuration) => {
    set((state) => ({
      videoDuration,
      ...(state.start_time === 0 &&
      state.end_time === 0 &&
      videoDuration !== undefined &&
      Number.isFinite(videoDuration) &&
      videoDuration > 0
        ? { end_time: videoDuration }
        : {}),
    }))
  },

  setVideoImageUrls: (videoImageUrls) => {
    if (videoImageUrls !== get().videoImageUrls) {
      productRequestGuard.invalidate()
      set({ videoImageUrls, isAnalyzingProduct: false, productInfoError: "" })
    }
  },

  setVideoOptions: (videoOptions, source = "user") => {
    set((state) => {
      const safeVideoOptions = getSafeVideoOptions(videoOptions)

      if (
        source === "user" &&
        state.taskId &&
        safeVideoOptions.model !== state.videoOptions.model
      ) {
        void saveHotVideoChannel(state.taskId, safeVideoOptions.model)
      }

      return { videoOptions: safeVideoOptions }
    })
  },

  setVideoUrl: (videoUrl) => {
    if (videoUrl !== get().videoUrl) {
      get().cancelRequests()
      if (!get().replicaPrompt.trim()) get().resetScript()
    }
    set((state) => ({
      videoUrl,
      ...(videoUrl !== state.videoUrl
        ? { start_time: 0, end_time: 0, videoDuration: undefined }
        : {}),
    }))
  },

  cancelRequests: () => {
    requestGuard.invalidate()
    artlineRequestGuard.invalidate()
    productRequestGuard.invalidate()
    set({
      isAnalyzingProduct: false,
      productInfoError: "",
      isGeneratingModelArtline: false,
      modelArtlineError: "",
      isRequesting: false,
      isRestoringTaskDraft: false,
      creatingPromise: null,
    })
  },

  resetScript: () => {
    promptSaver.reset()
    set((state) => ({
      ...getPromptState(),
      sourcePrompt: "",
      replicaPromptInputFingerprint: "",
      savedReferenceSettings: null,
      savedReferenceModel: null,
      hasEditedScript: false,
      canSaveScript: false,
      scriptRevision: state.scriptRevision + 1,
      taskErrorMessage: "",
      status: "",
      isSubmittingVideoTask: false,
      showStoryboardValidation: false,
    }))
  },

  ...createTaskDraftActions(set, get),
  ...createModelArtlineActions(set, get),
  ...createScriptActions(set, get),
  ...createVideoActions(set, get),
}))

bindScriptSaveState((state) => useHotVideoStore.setState(state))
